/**
 * Ephemeral keypair management for Free Speech Flags.
 * Each published flag can use a fresh keypair stored in localStorage.
 */
import { generateSecretKey, getPublicKey, finalizeEvent } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

const STORAGE_KEY = 'fsf:ephemeral-keys';

export interface EphemeralKeyEntry {
  flagId: string;       // the d-tag of the flag
  nsecHex: string;      // hex-encoded secret key (32 bytes)
  pubkey: string;       // hex public key
}

function loadKeys(): EphemeralKeyEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as EphemeralKeyEntry[];
  } catch {
    return [];
  }
}

function saveKeys(keys: EphemeralKeyEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
}

export function createEphemeralKey(flagId: string): EphemeralKeyEntry {
  const secretKey = generateSecretKey();
  const pubkey = getPublicKey(secretKey);
  const nsecHex = Array.from(secretKey).map(b => b.toString(16).padStart(2, '0')).join('');
  const entry: EphemeralKeyEntry = { flagId, nsecHex, pubkey };
  const keys = loadKeys().filter(k => k.flagId !== flagId);
  keys.push(entry);
  saveKeys(keys);
  return entry;
}

export function getEphemeralKey(flagId: string): EphemeralKeyEntry | undefined {
  return loadKeys().find(k => k.flagId === flagId);
}

export function getAllEphemeralKeys(): EphemeralKeyEntry[] {
  return loadKeys();
}

export function removeEphemeralKey(flagId: string) {
  const keys = loadKeys().filter(k => k.flagId !== flagId);
  saveKeys(keys);
}

/** Sign and return a finalized Nostr event using an ephemeral key */
export function signWithEphemeralKey(
  entry: EphemeralKeyEntry,
  template: Omit<NostrEvent, 'id' | 'sig' | 'pubkey'>
): NostrEvent {
  const secretKeyBytes = new Uint8Array(
    entry.nsecHex.match(/.{2}/g)!.map(h => parseInt(h, 16))
  );
  return finalizeEvent(
    {
      kind: template.kind,
      content: template.content,
      tags: template.tags,
      created_at: template.created_at ?? Math.floor(Date.now() / 1000),
    },
    secretKeyBytes
  ) as NostrEvent;
}
